import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/api';
import { getUrlParameters } from 'utils/helpers';
import {
  leftParenthesisAlternative,
  rightParenthesisAlternative,
  slashAlternative,
  ticketObjectFilterId,
  urlSigns,
} from '../../utils/app-configs';
import * as actionTypes from './constants';
import * as actions from './worklist-actions';

export function* getWorklistDefinitions(action) {
  try {
    const result = yield call(callApi, {
      endpoint: '/worklist/definitions',
      options: {
        errorMessage: 'Could not get worklist definitions',
        retryAction: action,
      },
    });
    yield put(actions.getWorklistDefinitionsSuccess(result));
  } catch (err) {
    yield put(actions.getWorklistDefinitionsFailure(err));
  }
}

export function* getWorklistData(action) {
  try {
    const {
      id,
      urlString = '',
      columnSort = '',
      needsGeoJson = false,
      objectId,
      shouldUpdateCount = false,
      newWorklist,
    } = action.payload;
    let filterString = urlString;
    if (objectId) {
      filterString =
        `${ticketObjectFilterId}${urlSigns.keyValueSeparator}${objectId}${filterString ?
          `${urlSigns.filterSeparator}${filterString}` :
          ''
        }`;
    }
    // Because parentheses can't be in the url, we use alternatives. We need to replace these
    // alternatives back to parentheses for the MW call
    filterString = filterString
      .replace(leftParenthesisAlternative, '(')
      .replace(rightParenthesisAlternative, ')')
      .replace(slashAlternative, '/');

    const result = yield call(callApi, {
      endpoint:
        // Hardcoded null is temporary until MW provides an endpoint to fetch the table metadata
        // In that case we'll  first fetch the metadata (including sort) and then fetch the data
        // So then  null is not needed
        // PBI: https://acop-dlwr.visualstudio.com/ACOP.CTSIntheCloud/_workitems/edit/13578
        // eslint-disable-next-line max-len
        `/worklist/${id}?filters=${filterString}&sort=${columnSort || 'null'}&geoJson=${needsGeoJson}`,
      options: {
        errorMessage: `Could not get worklist data: ${action.payload.id}`,
        retryAction: action,
      },
    });
    if (action.payload?.cb) {
      action.payload.cb(result || {});
    }
    yield put(actions.getWorklistDataSuccess({ ...result, shouldUpdateCount, newWorklist }));
  } catch (err) {
    if (action.payload?.cb) {
      action.payload.cb([]);
    }
    yield put(actions.getWorklistDataFailure(err));
  }
}

export function* getWorklistViews(action) {
  try {
    const { id } = action.payload;
    const result = yield call(callApi, {
      endpoint: `/worklist/${id}/template`,
      options: {
        errorMessage: `Could not get worklist views: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(actions.getWorklistViewsSuccess(result));
  } catch (err) {
    yield put(actions.getWorklistViewsFailure(err));
  }
}

export function* createWorklistView(action) {
  try {
    const {
      id,
      templateName,
      filterData,
      sorting,
      isDefault,
      columnInfo,
      childColumnInfo,
      isExpanded,
    } = action.payload;
    const request = {
      templateName,
      filterData,
      sorting,
      isDefault,
      columnInfo,
      childColumnInfo,
      isExpanded,
    };
    const result = yield call(callApi, {
      endpoint: `/worklist/${id}/template/new`,
      options: {
        method: 'POST',
        body: JSON.stringify(request),
        errorMessage: `Could not save worklist view: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(actions.createWorklistViewSuccess(result));
  } catch (err) {
    yield put(actions.createWorklistViewFailure(err));
  }
}

export function* getWorklistDataNext(action) {
  try {
    const next = yield select(state => state.worklist.next);
    if (next) {
      const result = yield call(callApi, {
        endpoint: `${next.uri.replace('api/', '/')}?${getUrlParameters({ params: next.query })}`,
        options: {
          errorMessage: `Could not next worklist data: ${next.uri}`,
          retryAction: action,
        },
      });
      if (action.payload?.cb) {
        action.payload.cb(result || {});
        if (action.payload.triggerHighlight) {
          yield put(actions.triggerRowHighlight(true));
        }
      }
      yield put(actions.getWorklistDataNextSuccess(result));
    }
  } catch (err) {
    if (action.payload?.cb) {
      action.payload.cb([]);
    }
    yield put(actions.getWorklistDataNextFailure(err));
  }
}

export function* updateWorklistViews(action) {
  try {
    const { id, views } = action.payload;

    const result = yield call(callApi, {
      endpoint: `/worklist/${id}/template`,
      options: {
        method: 'PUT',
        body: JSON.stringify(views),
        errorMessage: `Could not update worklist views: ${action.payload.id}`,
        retryAction: action,
      },
    });
    yield put(actions.updateWorklistViewsSuccess(result));
  } catch (err) {
    yield put(actions.updateWorklistViewsFailure(err));
  }
}

export function* getWorklistMetaData(action) {
  const { worklistId } = action.payload;
  try {
    const result = yield call(callApi, {
      endpoint: `/worklist/${worklistId}/tablemetadata`,
      options: {
        errorMessage: 'Could not get worklist metadata',
        retryAction: action,
      },
    });
    yield put(actions.getWorklistMetaDataSuccess(result));
  } catch (err) {
    yield put(actions.getWorklistMetaDataFailure(err));
  }
}

export function* getWorklistDataById(action) {
  try {
    const {
      ids,
      worklistId = 'serviceplanning',
      isDelete = false,
      highlightIds = [],
    } = action.payload;

    const result = yield call(callApi, {
      endpoint: `/worklist/${worklistId}/byid`,
      options: {
        method: 'POST',
        body: JSON.stringify({ ids, process: true }),
        errorMessage: `Could not get worklist data for: ${ids?.join(', ')}`,
        retryAction: action,
      },
    });
    yield put(actions.getWorklistDataByIdSuccess({
      data: result.data?.map(d => ({
        ...d,
        isDeleted: isDelete,
        highlighted: !!highlightIds?.find(i => i === d.id),
        children: d.children?.map(c => ({
          ...c,
          highlighted: !!highlightIds?.find(i => i === c.id),
        })),
      })),
    }));
  } catch (err) {
    yield put(actions.getWorklistDataByIdFailure(err));
  }
}

function* watchGetWorklistDefinitions() {
  yield takeLatest(actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST, getWorklistDefinitions);
}

function* watchGetWorklistData() {
  yield takeLatest(actionTypes.GET_WORKLIST_DATA_REQUEST, getWorklistData);
}

function* watchGetWorklistViews() {
  yield takeLatest(actionTypes.GET_WORKLIST_VIEWS_REQUEST, getWorklistViews);
}

function* watchUpdateWorklistViews() {
  yield takeLatest(actionTypes.UPDATE_WORKLIST_VIEWS_REQUEST, updateWorklistViews);
}

function* watchcreateWorklistView() {
  yield takeLatest(actionTypes.CREATE_WORKLIST_VIEW_REQUEST, createWorklistView);
}

function* watchGetWorklistDataNext() {
  yield takeLatest(actionTypes.GET_WORKLIST_DATA_NEXT_REQUEST, getWorklistDataNext);
}

function* watchGetWorklistMetaData() {
  yield takeLatest(actionTypes.GET_WORKLIST_METADATA_REQUEST, getWorklistMetaData);
}

function* watchGetWorklistDataById() {
  yield takeEvery(actionTypes.GET_WORKLIST_DATA_BY_ID_REQUEST, getWorklistDataById);
}

export default [
  fork(watchGetWorklistDefinitions),
  fork(watchGetWorklistData),
  fork(watchGetWorklistDataNext),
  fork(watchGetWorklistViews),
  fork(watchcreateWorklistView),
  fork(watchUpdateWorklistViews),
  fork(watchGetWorklistMetaData),
  fork(watchGetWorklistDataById),
];
